import { type FC, useState } from 'react'
import { Form } from 'react-bootstrap'
import { Button } from '../../../../components/Button/Button'
import { Modal } from '../../../../components/Modal/Modal'
import { MeetingMode, type SchedulingEventType } from '../../../../models/Scheduling'

const PAGE_SIZE = 5

interface AddEventTypesModalProps {
  candidates: SchedulingEventType[]
  isAdding: boolean
  onAdd: (eventTypes: SchedulingEventType[]) => void
  onCancel: () => void
}

const addLabel = (count: number): string => {
  if (count === 1) {
    return 'Add 1 event type'
  }
  return `Add ${count} event types`
}

export const AddEventTypesModal: FC<AddEventTypesModalProps> = ({ candidates, isAdding, onAdd, onCancel }) => {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const query = search.trim().toLowerCase()
  const matching = query ? candidates.filter((eventType) => eventType.name.toLowerCase().includes(query)) : candidates
  const totalPages = Math.max(1, Math.ceil(matching.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages - 1)
  const visible = matching.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE)
  const shownUpTo = Math.min((currentPage + 1) * PAGE_SIZE, matching.length)

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setPage(0)
  }

  const toggle = (eventTypeId: string) => {
    setSelectedIds((current) => (current.includes(eventTypeId) ? current.filter((id) => id !== eventTypeId) : [...current, eventTypeId]))
  }

  const handleAdd = () => {
    onAdd(candidates.filter((eventType) => selectedIds.includes(eventType.id)))
  }

  return (
    <Modal
      show
      onHide={onCancel}
      header={
        <div className='d-flex align-items-start zocks-gap-0.5'>
          <i className='bi bi-people-fill zocks-text-gray-900 mt-1' aria-hidden='true' />
          <div className='d-flex flex-column zocks-gap-0.25'>
            <h5 className='mb-0'>Add event types to the account booking page</h5>
            <span className='paragraph-small zocks-text-gray-600'>Clients can book these once they have hosts.</span>
          </div>
        </div>
      }
      body={
        <div className='d-flex flex-column gap-3'>
          <div className='d-flex align-items-center zocks-gap-1.25 border rounded-1 zocks-border-color-gray-400 zocks-px-0.75'>
            <i className='bi bi-search zocks-text-gray-500' aria-hidden='true' />
            <Form.Control
              type='search'
              autoComplete='off'
              value={search}
              onChange={(event) => handleSearchChange(event.currentTarget.value)}
              placeholder='Search'
              aria-label='Search'
              className='border-0 px-0 paragraph-small'
            />
          </div>
          <div className='d-flex align-items-center justify-content-between px-3 py-2 rounded-2 zocks-bg-color-gray-100'>
            <span className='paragraph-small zocks-text-gray-700'>{`Showing ${shownUpTo} of ${matching.length}`}</span>
            <div className='d-flex align-items-center gap-1'>
              <Button variant='grayButton' className='p-1' aria-label='Previous page' disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>
                <i className='bi bi-chevron-left' aria-hidden='true' />
              </Button>
              <Button
                variant='grayButton'
                className='p-1'
                aria-label='Next page'
                disabled={currentPage + 1 >= totalPages}
                onClick={() => setPage(currentPage + 1)}
              >
                <i className='bi bi-chevron-right' aria-hidden='true' />
              </Button>
            </div>
          </div>
          {visible.length === 0 ? (
            <div className='paragraph-small zocks-text-gray-600 text-center py-4'>
              {candidates.length === 0 ? 'Every shared event type is already on this page.' : 'No event type matches your search.'}
            </div>
          ) : (
            <div className='d-flex flex-column'>
              {visible.map((eventType) => {
                const checkboxId = `add-event-type-${eventType.id}`
                return (
                  <div key={eventType.id} className='d-flex align-items-center gap-4 py-3 border-bottom zocks-border-color-gray-200'>
                    <Form.Check
                      type='checkbox'
                      id={checkboxId}
                      checked={selectedIds.includes(eventType.id)}
                      onChange={() => toggle(eventType.id)}
                      aria-label={eventType.name}
                    />
                    <label htmlFor={checkboxId} className='d-flex flex-column'>
                      <span className='fw-bold'>{eventType.name}</span>
                      <span className='paragraph-small zocks-text-gray-700'>
                        {`${eventType.durationMinutes} min · ${eventType.meetingMode === MeetingMode.IN_PERSON ? 'In person' : 'Virtual'}`}
                      </span>
                    </label>
                  </div>
                )
              })}
            </div>
          )}
          <div className='d-flex align-items-center zocks-gap-0.5 paragraph-small zocks-text-gray-600'>
            <i className='bi bi-info-circle flex-shrink-0' aria-hidden='true' />
            Nothing goes live until you choose who takes its bookings.
          </div>
        </div>
      }
      showFooterActions
      footerPrimaryActionTitle={addLabel(selectedIds.length)}
      footerSecondaryActionTitle='Cancel'
      footerPrimaryActionDisabled={selectedIds.length === 0}
      footerPrimaryActionLoading={isAdding}
      onFooterPrimaryAction={handleAdd}
      onFooterSecondaryAction={onCancel}
    />
  )
}
